import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Menu,
  HomeIcon,
  Tag,
  MessageCircle,
  Users,
  Gamepad2,
  ChevronLeft,
  ShieldAlert,
  Activity,
  Trophy,
} from 'lucide-react';
import cn from '../../../lib/utils';
import { Button } from '../../ui/button';
import useUserContext from '../../../hooks/useUserContext';

/**
 * The SideBarNav component has menu items: "Questions", "Tags", "Messaging", "Users", and "Games".
 * It provides a responsive sidebar that can be collapsed to icons only or expanded to show text.
 * It highlights the currently selected item and allows for expandable navigation options.
 */
const SideBarNav = () => {
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isMobileView, setIsMobileView] = useState<boolean>(false);
  const location = useLocation();
  const { user } = useUserContext();

  // Check window width on mount and resize
  useEffect(() => {
    const handleResize = () => {
      const mobileWidth = window.innerWidth < 768;
      setIsMobileView(mobileWidth);

      if (mobileWidth) {
        setIsExpanded(true); // Always show text on mobile
      } else {
        setIsExpanded(true);
      }
    };

    // Set initial state
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const toggleSidebar = () => {
    if (!isMobileView) {
      setIsExpanded(!isExpanded);
    }
  };

  const isActiveOption = (path: string) => location.pathname === path;

  return (
    <div
      className={cn(
        'border-r border-dashed border-gray-300 py-3 transition-all duration-300 flex flex-col',
        isMobileView ? 'h-auto w-full pb-1' : 'h-screen',
        !isMobileView && (isExpanded ? 'w-64' : 'w-16'),
      )}>
      {!isMobileView && (
        <div className={cn('flex', isExpanded ? 'justify-end' : 'justify-center', 'px-2 mb-3')}>
          <Button variant='ghost' size='sm' className='p-1 h-8 w-8' onClick={toggleSidebar}>
            {isExpanded ? <ChevronLeft size={18} /> : <Menu size={18} />}
          </Button>
        </div>
      )}

      <div
        className={cn(
          'flex items-center',
          isMobileView
            ? 'flex-row justify-around w-full space-y-0 overflow-x-auto'
            : 'flex-col flex-1 space-y-2',
        )}>
        <NavLink
          to='/home'
          id='menu_questions'
          className={({ isActive }) =>
            cn(
              'flex items-center px-3 py-2 rounded-md transition-colors',
              isMobileView ? 'justify-center mx-1 flex-col text-xs' : 'w-full mx-auto',
              !isMobileView && (isExpanded ? 'justify-start pl-6' : 'justify-center'),
              isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
            )
          }>
          <HomeIcon size={isMobileView ? 16 : 20} />
          {(isExpanded || isMobileView) && (
            <span className={cn(isMobileView ? 'mt-1' : 'ml-3')}>
              {isMobileView ? 'Home' : 'Questions'}
            </span>
          )}
        </NavLink>

        <NavLink
          to='/tags'
          id='menu_tag'
          className={({ isActive }) =>
            cn(
              'flex items-center px-3 py-2 rounded-md transition-colors',
              isMobileView ? 'justify-center mx-1 flex-col text-xs' : 'w-full mx-auto',
              !isMobileView && (isExpanded ? 'justify-start pl-6' : 'justify-center'),
              isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
            )
          }>
          <Tag size={isMobileView ? 16 : 20} />
          {(isExpanded || isMobileView) && (
            <span className={cn(isMobileView ? 'mt-1' : 'ml-3')}>Tags</span>
          )}
        </NavLink>

        <NavLink
          to='/messaging'
          id='menu_messaging'
          className={({ isActive }) =>
            cn(
              'flex items-center px-3 py-2 rounded-md transition-colors',
              isMobileView ? 'justify-center mx-1 flex-col text-xs' : 'w-full mx-auto',
              !isMobileView && (isExpanded ? 'justify-between pl-6 pr-4' : 'justify-center'),
              isActive || isActiveOption('/messaging/direct-message')
                ? 'bg-gray-200 text-blue-700'
                : 'text-blue-500 hover:bg-gray-100',
            )
          }
          onClick={isMobileView ? undefined : toggleOptions}>
          <div className={cn('flex items-center', isMobileView && 'flex-col')}>
            <MessageCircle size={isMobileView ? 16 : 20} />
            {(isExpanded || isMobileView) && (
              <span className={cn(isMobileView ? 'mt-1' : 'ml-3')}>
                {isMobileView ? 'Messages' : 'Messaging'}
              </span>
            )}
          </div>
          {!isMobileView &&
            isExpanded &&
            (showOptions ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
        </NavLink>

        {showOptions && isExpanded && !isMobileView && (
          <div className='w-full pl-10 space-y-1'>
            <NavLink
              to='/messaging'
              className={cn(
                'w-full flex items-center px-3 py-2 rounded-md transition-colors',
                isActiveOption('/messaging')
                  ? 'bg-blue-500 text-white'
                  : 'text-blue-500 hover:bg-gray-100',
              )}>
              <span>Global Messages</span>
            </NavLink>
            <NavLink
              to='/messaging/direct-message'
              className={cn(
                'w-full flex items-center px-3 py-2 rounded-md transition-colors',
                isActiveOption('/messaging/direct-message')
                  ? 'bg-blue-500 text-white'
                  : 'text-blue-500 hover:bg-gray-100',
              )}>
              <span>Direct Messages</span>
            </NavLink>
          </div>
        )}

        <NavLink
          to='/users'
          id='menu_users'
          className={({ isActive }) =>
            cn(
              'flex items-center px-3 py-2 rounded-md transition-colors',
              isMobileView ? 'justify-center mx-1 flex-col text-xs' : 'w-full mx-auto',
              !isMobileView && (isExpanded ? 'justify-start pl-6' : 'justify-center'),
              isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
            )
          }>
          <Users size={isMobileView ? 16 : 20} />
          {(isExpanded || isMobileView) && (
            <span className={cn(isMobileView ? 'mt-1' : 'ml-3')}>Users</span>
          )}
        </NavLink>

        <NavLink
          to='/games'
          id='menu_games'
          className={({ isActive }) =>
            cn(
              'flex items-center px-3 py-2 rounded-md transition-colors',
              isMobileView ? 'justify-center mx-1 flex-col text-xs' : 'w-full mx-auto',
              !isMobileView && (isExpanded ? 'justify-start pl-6' : 'justify-center'),
              isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
            )
          }>
          <Gamepad2 size={isMobileView ? 16 : 20} />
          {(isExpanded || isMobileView) && (
            <span className={cn(isMobileView ? 'mt-1' : 'ml-3')}>Games</span>
          )}
        </NavLink>

        <NavLink
          to='/activity'
          className={({ isActive }) =>
            cn(
              'flex items-center px-3 py-2 rounded-md transition-colors',
              isMobileView ? 'justify-center mx-1 flex-col text-xs' : 'w-full mx-auto',
              !isMobileView && (isExpanded ? 'justify-start pl-6' : 'justify-center'),
              isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
            )
          }>
          <Activity size={isMobileView ? 16 : 20} />
          {(isExpanded || isMobileView) && (
            <span className={cn(isMobileView ? 'mt-1' : 'ml-3')}>Activity Feed</span>
          )}
        </NavLink>

        <NavLink
          to='/leaderboard'
          className={({ isActive }) =>
            cn(
              'flex items-center px-3 py-2 rounded-md transition-colors',
              isMobileView ? 'justify-center mx-1 flex-col text-xs' : 'w-full mx-auto',
              !isMobileView && (isExpanded ? 'justify-start pl-6' : 'justify-center'),
              isActive ? 'bg-gray-200 text-blue-700' : 'text-blue-500 hover:bg-gray-100',
            )
          }>
          <Trophy size={isMobileView ? 16 : 20} />
          {(isExpanded || isMobileView) && (
            <span className={cn(isMobileView ? 'mt-1' : 'ml-3')}>Leaderboard</span>
          )}
        </NavLink>

        {user.isAdmin && (
          <NavLink
            to='/admin'
            id='menu_admin'
            className={({ isActive }) =>
              cn(
                'flex items-center px-3 py-2 rounded-md transition-colors',
                isMobileView ? 'justify-center mx-1 flex-col text-xs' : 'w-full mx-auto',
                !isMobileView && (isExpanded ? 'justify-start pl-6' : 'justify-center'),
                isActive ? 'bg-red-100 text-red-700' : 'text-red-600 hover:bg-red-50',
              )
            }>
            <ShieldAlert size={isMobileView ? 16 : 20} />
            {(isExpanded || isMobileView) && (
              <span className={cn(isMobileView ? 'mt-1' : 'ml-3')}>Admin</span>
            )}
          </NavLink>
        )}
      </div>
    </div>
  );
};

export default SideBarNav;
